import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./FlightMap.css";

const mockMapData = {
  orig: { lat: 46.23809814, lon: 6.108950138 },
  dest: { lat: 49.49869919, lon: 11.06690025 },
  color: "#4A90E2",
  originIcao: "LSGG",
  destinationIcao: "EDDN"
};

const FlightMap = (props) => {
  const [mapData, setMapData] = useState(mockMapData);
  const mapElementRef = useRef(null);
  const infoRef = useRef(null);
  const mapRef = useRef(null);
  const layerGroupRef = useRef(null);

  useEffect(() => {
    if (props && props.orig && props.dest) {
      setMapData(props);
    }
  }, [props]);

  const handleMarkerHover = (icaoCode) => (e) => {
    const nativeEvent = e && e.originalEvent ? e.originalEvent : null;
    if (!infoRef.current) return;
    infoRef.current.style.display = "block";
    infoRef.current.style.padding = "0.5rem";
    if (nativeEvent) {
      infoRef.current.style.top = nativeEvent.clientY + "px";
      infoRef.current.style.left = nativeEvent.clientX + "px";
    }
    infoRef.current.innerHTML = icaoCode || "";
  };

  const handleMarkerRemoval = () => {
    if (!infoRef.current) return;
    infoRef.current.innerHTML = "";
    infoRef.current.style.padding = "0";
    infoRef.current.style.display = "none";
  };

  useEffect(() => {
    if (!mapElementRef.current || mapRef.current) return;

    const map = L.map(mapElementRef.current, {
      center: [38, -95],
      zoom: 4,
      zoomControl: true,
      scrollWheelZoom: true
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(map);

    mapRef.current = map;
    layerGroupRef.current = L.layerGroup().addTo(map);

    map.whenReady(() => {
      map.invalidateSize();
    });

    let resizeObserver = null;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        if (mapRef.current) {
          mapRef.current.invalidateSize();
        }
      });
      resizeObserver.observe(mapElementRef.current);
    }

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      layerGroupRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const group = layerGroupRef.current;
    if (!map || !group || !mapData || !mapData.orig || !mapData.dest) return;

    const originPos = [Number(mapData.orig.lat), Number(mapData.orig.lon)];
    const destPos = [Number(mapData.dest.lat), Number(mapData.dest.lon)];

    if (
      !Number.isFinite(originPos[0]) ||
      !Number.isFinite(originPos[1]) ||
      !Number.isFinite(destPos[0]) ||
      !Number.isFinite(destPos[1])
    ) {
      return;
    }

    group.clearLayers();

    const originMarker = L.circleMarker(originPos, {
      radius: 8,
      color: "#ffffff",
      weight: 2,
      fillColor: "#10b981",
      fillOpacity: 1
    })
      .on("mouseover", handleMarkerHover(mapData.originIcao))
      .on("mouseout", handleMarkerRemoval)
      .bindTooltip(mapData.originIcao || "ORIG", {
        direction: "top",
        offset: [0, -8],
        permanent: true,
        className: "flight-map-point-label"
      });

    const destinationMarker = L.circleMarker(destPos, {
      radius: 8,
      color: "#ffffff",
      weight: 2,
      fillColor: "#ef4444",
      fillOpacity: 1
    })
      .on("mouseover", handleMarkerHover(mapData.destinationIcao))
      .on("mouseout", handleMarkerRemoval)
      .bindTooltip(mapData.destinationIcao || "DEST", {
        direction: "top",
        offset: [0, -8],
        permanent: true,
        className: "flight-map-point-label"
      });

    const flightLine = L.polyline([originPos, destPos], {
      color: mapData.color || "#4A90E2",
      weight: 3,
      dashArray: "8 4"
    });

    originMarker.addTo(group);
    destinationMarker.addTo(group);
    flightLine.addTo(group);

    map.invalidateSize();
    const bounds = L.latLngBounds(originPos, destPos);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 8 });
  }, [mapData]);

  return (
    <React.Fragment>
      <div ref={infoRef} className="map-info" />
      <div className="map-main" style={{ position: "relative" }}>
        <div ref={mapElementRef} className="leaflet-flight-map" />
      </div>
    </React.Fragment>
  );
};

export default React.memo(FlightMap);
